import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { UserPlus, Search, CheckCircle, MessageSquare, ArrowRight, Building2, TrendingUp, MapPin, Tag } from "lucide-react";

const steps = [
  {
    id: 1,
    label: "Build Your Profile",
    icon: UserPlus,
    description: "Founders and investors complete a structured onboarding. Sector, stage, and thesis are all defined upfront.",
    visual: <ProfileCard />,
  },
  {
    id: 2,
    label: "Discover Opportunities",
    icon: Search,
    description: "Our engine surfaces relevant connections based on investment fit. Review in seconds, not days.",
    visual: <DiscoverCard />,
  },
  {
    id: 3,
    label: "Signal Interest",
    icon: CheckCircle,
    description: "Express interest or pass. When both sides align, a mutual connection is confirmed.",
    visual: <MatchCard />,
  },
  {
    id: 4,
    label: "Connect & Close",
    icon: MessageSquare,
    description: "Unlock direct messaging and built-in deal tools, including integrated wallet infrastructure.",
    visual: <ConnectCard />,
  },
];

function ProfileCard() {
  return (
    <div className="glass rounded-2xl p-6 space-y-4 w-full max-w-sm mx-auto">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
          <Building2 className="w-6 h-6 text-primary" />
        </div>
        <div>
          <p className="font-semibold text-foreground text-sm">NovaPay</p>
          <p className="text-xs text-muted-foreground">FinTech · Seed Stage</p>
        </div>
      </div>
      <div className="space-y-2">
        {[
          { label: "Sector", value: "B2B Payments" },
          { label: "Raise", value: "$2.5M" },
          { label: "Traction", value: "$180K ARR" },
        ].map((item) => (
          <div key={item.label} className="flex justify-between text-xs">
            <span className="text-muted-foreground">{item.label}</span>
            <span className="text-foreground font-medium">{item.value}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        {["Payments", "Africa", "API-first"].map((tag) => (
          <span key={tag} className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary border border-primary/20">
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

function DiscoverCard() {
  return (
    <div className="w-full max-w-sm mx-auto space-y-3">
      {[
        { name: "Meridian Ventures", type: "Investor", connection: "94%", focus: "FinTech · Seed" },
        { name: "Atlas Capital", type: "Investor", connection: "87%", focus: "B2B · Series A" },
        { name: "Kora Fund", type: "Investor", connection: "81%", focus: "Africa · Early" },
      ].map((card, i) => (
        <motion.div
          key={card.name}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: i * 0.15 }}
          className="glass rounded-xl p-4 flex items-center gap-3"
        >
          <div className="w-9 h-9 rounded-full bg-secondary/20 flex items-center justify-center shrink-0">
            <TrendingUp className="w-4 h-4 text-secondary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{card.name}</p>
            <p className="text-xs text-muted-foreground">{card.focus}</p>
          </div>
          <span className="text-xs font-bold text-primary bg-primary/10 border border-primary/20 px-2 py-1 rounded-full shrink-0">
            {card.connection}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

function MatchCard() {
  return (
    <div className="w-full max-w-sm mx-auto">
      <div className="glass rounded-2xl p-6 text-center space-y-4">
        <div className="flex items-center justify-center gap-4">
          <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
            <Building2 className="w-7 h-7 text-primary" />
          </div>
          <motion.div
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ repeat: Infinity, duration: 2 }}
            className="w-8 h-8 rounded-full bg-primary flex items-center justify-center"
          >
            <CheckCircle className="w-5 h-5 text-primary-foreground" />
          </motion.div>
          <div className="w-14 h-14 rounded-full bg-secondary/20 flex items-center justify-center">
            <TrendingUp className="w-7 h-7 text-secondary" />
          </div>
        </div>
        <div>
          <p className="text-sm font-bold text-primary">Mutual Connection</p>
          <p className="text-xs text-muted-foreground mt-1">NovaPay × Meridian Ventures</p>
        </div>
        <div className="flex justify-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> London, United Kingdom</span>
          <span className="flex items-center gap-1"><Tag className="w-3 h-3" /> Seed · $2.5M</span>
        </div>
      </div>
    </div>
  );
}

function ConnectCard() {
  return (
    <div className="w-full max-w-sm mx-auto glass rounded-2xl p-4 space-y-3">
      <div className="flex items-center gap-2 border-b border-border/50 pb-3">
        <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
          <Building2 className="w-4 h-4 text-primary" />
        </div>
        <p className="text-xs font-semibold text-foreground">NovaPay × Meridian Ventures</p>
      </div>
      <div className="space-y-2">
        {[
          { from: "Meridian", text: "Excited to connect! Can you share your latest deck?", mine: false },
          { from: "NovaPay", text: "Of course, sending via the deal room now.", mine: true },
          { from: "Meridian", text: "Perfect. Let's schedule a call this week.", mine: false },
        ].map((msg, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.2 }}
            className={`flex ${msg.mine ? "justify-end" : "justify-start"}`}
          >
            <div className={`text-xs px-3 py-2 rounded-xl max-w-[75%] ${msg.mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
              {msg.text}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

const DemoSection = () => {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section className="py-24 md:py-32 bg-muted/10">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-4">
            See How It <span className="text-gradient">Works</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            From profile to closed deal: a structured, four-step process designed for speed and precision.
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          {/* Step selector */}
          <div className="space-y-3">
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isActive = activeStep === i;
              return (
                <motion.button
                  key={step.id}
                  onClick={() => setActiveStep(i)}
                  className={`w-full text-left rounded-xl p-4 border transition-all duration-300 flex items-start gap-4 ${
                    isActive
                      ? "bg-primary/10 border-primary/40 shadow-[0_0_20px_hsl(174_62%_47%/0.1)]"
                      : "glass border-border/50 hover:border-primary/20"
                  }`}
                  whileHover={{ x: isActive ? 0 : 4 }}
                >
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 mt-0.5 transition-colors ${isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`}>Step {step.id}</span>
                    </div>
                    <p className={`font-display font-semibold text-sm mb-1 ${isActive ? "text-foreground" : "text-muted-foreground"}`}>{step.label}</p>
                    {isActive && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="text-xs text-muted-foreground leading-relaxed"
                      >
                        {step.description}
                      </motion.p>
                    )}
                  </div>
                  {isActive && <ArrowRight className="w-4 h-4 text-primary shrink-0 mt-2" />}
                </motion.button>
              );
            })}
          </div>

          {/* Visual panel */}
          <div className="relative min-h-[300px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                initial={{ opacity: 0, y: 20, scale: 0.97 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.97 }}
                transition={{ duration: 0.3 }}
                className="w-full"
              >
                {steps[activeStep].visual}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;
